/**
 * Sistema de colores para horas estimadas en asignaciones
 * Proporciona funciones utilitarias para determinar colores basados en carga de trabajo
 */

export interface ColorConfig {
  backgroundColor: string;
  textColor: string;
  description: string;
}

export interface HourRange {
  min: number;
  max: number;
  color: string;
  description: string;
}

// Configuración de rangos para horas diarias
export const DAILY_HOUR_RANGES: HourRange[] = [
  { min: 0, max: 0, color: '#FF8C00', description: 'Sin asignación' },
  { min: 0.1, max: 7.9, color: '#FFD700', description: 'Carga baja' },
  { min: 8, max: 9, color: '#32CD32', description: 'Carga óptima' },
  { min: 9.1, max: Infinity, color: '#FF4444', description: 'Sobrecarga' }
];

// Configuración de rangos para horas semanales
export const WEEKLY_HOUR_RANGES: HourRange[] = [
  { min: 0, max: 0, color: '#FF8C00', description: 'Sin asignaciones' },
  { min: 0.1, max: 39.9, color: '#FFD700', description: 'Carga semanal baja' },
  { min: 40, max: 45, color: '#32CD32', description: 'Carga semanal óptima' },
  { min: 45.1, max: Infinity, color: '#FF4444', description: 'Sobrecarga semanal' }
];

/**
 * Determina el color de fondo basado en las horas y rangos proporcionados
 */
export function getHoursBackgroundColor(hours: number, ranges: HourRange[]): string {
  const range = ranges.find(r => hours >= r.min && hours <= r.max);
  return range?.color || '#FFD700'; // Amarillo por defecto
}

/**
 * Determina el color del texto basado en el color de fondo para óptimo contraste
 */
export function getTextColor(backgroundColor: string): string {
  // Colores que requieren texto blanco para mejor contraste
  const darkBackgrounds = ['#FF4444'];
  return darkBackgrounds.includes(backgroundColor) ? '#FFFFFF' : '#000000';
}

/**
 * Obtiene la configuración completa de color para horas diarias
 */
export function getDailyHoursColorConfig(hours: number): ColorConfig {
  const backgroundColor = getHoursBackgroundColor(hours, DAILY_HOUR_RANGES);
  const textColor = getTextColor(backgroundColor);
  const range = DAILY_HOUR_RANGES.find(r => hours >= r.min && hours <= r.max);
  
  return {
    backgroundColor,
    textColor,
    description: range?.description || 'Carga desconocida'
  };
}

/**
 * Obtiene la configuración completa de color para horas semanales
 */
export function getWeeklyHoursColorConfig(hours: number): ColorConfig {
  const backgroundColor = getHoursBackgroundColor(hours, WEEKLY_HOUR_RANGES);
  const textColor = getTextColor(backgroundColor);
  const range = WEEKLY_HOUR_RANGES.find(r => hours >= r.min && hours <= r.max);
  
  return {
    backgroundColor,
    textColor,
    description: range?.description || 'Carga desconocida'
  };
}

/**
 * Genera un tooltip descriptivo para las horas
 */
export function generateHoursTooltip(hours: number, isWeekly: boolean = false): string {
  const config = isWeekly ? getWeeklyHoursColorConfig(hours) : getDailyHoursColorConfig(hours);
  const period = isWeekly ? 'semanal' : '';
  const hoursText = isWeekly ? `Total ${period} estimado` : 'Horas estimadas';
  
  return `${hoursText}: ${hours.toFixed(1)}h - ${config.description}`;
}

/**
 * Estilos CSS inline para aplicar a elementos con colores dinámicos
 */
export function getHoursElementStyles(hours: number, isWeekly: boolean = false): React.CSSProperties {
  const config = isWeekly ? getWeeklyHoursColorConfig(hours) : getDailyHoursColorConfig(hours);
  
  return {
    backgroundColor: config.backgroundColor,
    color: config.textColor,
    fontWeight: isWeekly ? '700' : '600'
  };
}

/**
 * Valida si las horas están en un rango saludable
 */
export function isHealthyWorkload(hours: number, isWeekly: boolean = false): boolean {
  if (isWeekly) {
    return hours >= 40 && hours <= 45;
  }
  return hours >= 8 && hours <= 9;
}

/**
 * Obtiene el nivel de alerta basado en las horas
 */
export function getWorkloadAlertLevel(hours: number, isWeekly: boolean = false): 'none' | 'low' | 'optimal' | 'high' {
  const ranges = isWeekly ? WEEKLY_HOUR_RANGES : DAILY_HOUR_RANGES;
  const range = ranges.find(r => hours >= r.min && hours <= r.max);
  
  if (!range) return 'none';
  
  switch (range.color) {
    case '#FF8C00': return 'none';     // Naranja - sin asignación
    case '#FFD700': return 'low';      // Amarillo - carga baja
    case '#32CD32': return 'optimal';  // Verde - óptima
    case '#FF4444': return 'high';     // Rojo - sobrecarga
    default: return 'none';
  }
}